import React, { useState } from "react";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  InfoWindow,
  Circle,
} from "@react-google-maps/api";
import { PropertyToEdit } from "redux/reducers/Properties/PropertiesSliceTypes";
import { MapPin, Bed, Users, Home } from "lucide-react";

interface ListingMapProps {
  propertyData: PropertyToEdit | null;
  userLocation: {
    lat: number;
    lng: number;
  };
}

const ListingMap: React.FC<ListingMapProps> = ({
  propertyData,
  userLocation,
}) => {
  const [showInfo, setShowInfo] = useState(false);
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY || "",
    libraries: ["places"],
  });

  const shortAddress = `${propertyData?.city}, ${propertyData?.state}`;

  if (loadError) {
    return (
      <div className="listingSection__wrap w-full">
        <div className="flex items-center justify-center py-20 bg-red-50 rounded-2xl border border-red-200">
          <div className="text-center">
            <p className="text-red-600 font-medium">Error loading map</p>
            <p className="text-sm text-red-500 mt-1">Please try refreshing the page</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="listingSection__wrap w-full">
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center space-y-3">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-primary-600 rounded-full animate-spin"></div>
            <p className="text-sm text-gray-500">Loading map...</p>
          </div>
        </div>
      </div>
    );
  }

  const mapContainerStyle = {
    width: "100%",
    height: "100%",
  };

  return (
    <div className="listingSection__wrap w-full">
      {/* Header Section */}
      <div className="space-y-3">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Where you'll be
        </h2>
        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
          <MapPin size={18} className="text-primary-600" />
          <span className="text-base">
            {shortAddress || "Location not available"}
          </span>
        </div>
      </div>

      <div className="w-full h-px bg-gradient-to-r from-transparent via-neutral-300 dark:via-neutral-700 to-transparent my-6" />

      {/* Map Container */}
      <div className="relative rounded-2xl overflow-hidden shadow-lg ring-1 ring-black/5">
        <div className="aspect-w-16 aspect-h-9 lg:aspect-h-7">
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            zoom={13}
            center={userLocation}
            options={{
              maxZoom: 15,
              minZoom: 12,
              zoomControl: true,
              mapTypeControl: false,
              streetViewControl: false,
              fullscreenControl: true,
              disableDefaultUI: false,
              keyboardShortcuts: false,
              styles: [
                {
                  featureType: "all",
                  elementType: "geometry",
                  stylers: [{ color: "#f5f5f5" }],
                },
                {
                  featureType: "water",
                  elementType: "geometry",
                  stylers: [{ color: "#c9e6f2" }],
                },
                {
                  featureType: "water",
                  elementType: "labels.text.fill",
                  stylers: [{ color: "#7c9fb3" }],
                },
                {
                  featureType: "road",
                  elementType: "geometry",
                  stylers: [{ color: "#ffffff" }],
                },
                {
                  featureType: "road",
                  elementType: "labels.text.fill",
                  stylers: [{ color: "#5f6368" }],
                },
                {
                  featureType: "road.highway",
                  elementType: "geometry",
                  stylers: [{ color: "#fee9d7" }],
                },
                {
                  featureType: "road.highway",
                  elementType: "labels.text.fill",
                  stylers: [{ color: "#8a6e3e" }],
                },
                {
                  featureType: "poi",
                  elementType: "geometry",
                  stylers: [{ color: "#e8f5e9" }],
                },
                {
                  featureType: "poi.park",
                  elementType: "geometry",
                  stylers: [{ color: "#c8e6c9" }],
                },
                {
                  featureType: "poi",
                  elementType: "labels.text.fill",
                  stylers: [{ color: "#5f6368" }],
                },
                {
                  featureType: "transit",
                  elementType: "geometry",
                  stylers: [{ visibility: "simplified" }],
                },
                {
                  featureType: "administrative",
                  elementType: "labels.text.fill",
                  stylers: [{ color: "#9e9e9e" }],
                },
              ],
            }}
          >
            {/* Custom Marker */}
            <Marker 
              position={userLocation} 
              onClick={() => setShowInfo(true)}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 10,
                fillColor: "#FF385C",
                fillOpacity: 1,
                strokeWeight: 3,
                strokeColor: "#ffffff",
              }}
            >
              {showInfo && (
                <InfoWindow
                  position={userLocation}
                  onCloseClick={() => setShowInfo(false)}
                  options={{
                    pixelOffset: new window.google.maps.Size(0, -15),
                  }}
                >
                  <div className="w-80 bg-white rounded-xl overflow-hidden shadow-lg font-sans">
                    {/* Image Section */}
                    <div className="relative h-44">
                      <img
                        src={propertyData?.place_images[0]?.url}
                        alt={propertyData?.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-white/95 text-gray-900 shadow-sm backdrop-blur-sm">
                          {propertyData?.property_state}
                        </span>
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="p-4 space-y-3">
                      {/* Title and Price */}
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="text-base font-semibold text-gray-900 line-clamp-1 flex-1">
                          {propertyData?.title}
                        </h3>
                        <div className="text-right flex-shrink-0">
                          <p className="text-lg font-bold text-gray-900">
                            ${propertyData?.price_per_month}
                          </p>
                          <p className="text-xs text-gray-500">per month</p>
                        </div>
                      </div>

                      {/* Property Type */}
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Home size={14} />
                        <span>
                          {propertyData?.property_type?.name} · {propertyData?.property_category?.name}
                        </span>
                      </div>

                      {/* Stats */}
                      <div className="flex items-center gap-4 pt-2 border-t border-gray-100">
                        <div className="flex items-center gap-1.5 text-gray-700">
                          <Bed size={16} className="text-gray-500" />
                          <span className="text-sm font-medium">
                            {propertyData?.total_bedrooms} beds
                          </span>
                        </div>
                        <div className="w-px h-4 bg-gray-300"></div>
                        <div className="flex items-center gap-1.5 text-gray-700">
                          <Users size={16} className="text-gray-500" />
                          <span className="text-sm font-medium">
                            {propertyData?.guest_number} guests
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </InfoWindow>
              )}
            </Marker>

            {/* Circle Overlay */}
            <Circle
              center={userLocation}
              radius={500}
              options={{
                fillColor: "#FF385C",
                fillOpacity: 0.12,
                strokeColor: "#FF385C",
                strokeOpacity: 0.4,
                strokeWeight: 2,
              }}
            />
          </GoogleMap>
        </div>

        {/* Overlay Info Badge */}
        <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg border border-gray-200">
          <p className="text-sm font-medium text-gray-900">
            Approximate location
          </p>
        </div>
      </div>

      {/* Additional Info */}
      <div className="mt-6 p-4 bg-gray-50 dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
          About this location
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          The exact address will be shared after your booking is confirmed. 
          The map shows the general area for privacy reasons.
        </p>
      </div>
    </div>
  );
};

export default ListingMap;